import { useMemo } from 'react';
import { useInventoryAll, useInventoryCount, useInventoryStates } from '@/hooks/inventory';
import { isMissingStateError } from '@/hooks/inventory/missing-state';
import { useInventoryFilterStore } from '@/stores/inventory/inventory-filter-store';
import { SLBPosition } from '@/types/inventory.types';
import { groupInventoryBySymbol, transformInventoryItemToSLBPosition, getDefaultStateId } from '@/utils/inventory';
import type { SearchItem } from '../useCreateTradeForm';

/**
 * LR-495: the inventory search-index pipeline extracted verbatim from
 * useCreateTradeForm. Subscribes to the inventory filter store, fetches the
 * current state's inventory and derives the dropdown's searchable items. Pure
 * data-derive (no side effects); memo chain and dep arrays preserved 1:1 so the
 * dropdown and `allSLBPositions` (consumed by handleSelectSecurity) behave
 * identically.
 */
export function useInventorySearchIndex(
  securitySearchQuery: string,
  /** Off while the form is closed: the create-trade modal is mounted on every page, and
   *  the whole inventory (~18 MB on the stands) would otherwise load under all of them. */
  enabled: boolean = true
): {
  allSLBPositions: SLBPosition[];
  filteredInventoryItems: SearchItem[];
  isDataLoading: boolean;
} {
  const inventoryFilters = useInventoryFilterStore((state) => state.inventoryFilters);
  const { data: statesData, isLoading: isStatesLoading, isFetching: isStatesFetching } = useInventoryStates();
  // A state pinned on the Inventory page may since have been deleted (intraday states are
  // replaced each collection); re-reading the states list can't move a pin, so drop it here.
  const currentStateId = useMemo(() => {
    const states = statesData || [];
    const pinned = inventoryFilters.state;
    return pinned && states.some((s) => s.id === pinned) ? pinned : getDefaultStateId(states);
  }, [inventoryFilters.state, statesData]);
  const hasState = currentStateId !== undefined;
  const apiParams = useMemo(
    () => (currentStateId !== undefined ? { ...inventoryFilters, state: currentStateId } : undefined),
    [inventoryFilters, currentStateId]
  );
  const { data: countData, isLoading: isCountLoading, error: countError } = useInventoryCount(
    apiParams,
    enabled && hasState
  );
  const { data: allInventoryData, isLoading: isInventoryLoading, error: inventoryError } = useInventoryAll(
    apiParams,
    countData?.count,
    enabled && hasState && countData !== undefined
  );
  // A 404 re-reads the states list in the background (isLoading stays false: the list has
  // data); until it lands and re-keys the queries, the empty result is not "no securities".
  const isReresolvingState =
    isStatesFetching && (isMissingStateError(countError) || isMissingStateError(inventoryError));
  const isDataLoading = isStatesLoading || isCountLoading || isInventoryLoading || isReresolvingState;
  const allSLBPositions = useMemo(() => {
    if (!allInventoryData) return [];
    return allInventoryData.map(transformInventoryItemToSLBPosition);
  }, [allInventoryData]);
  const groupedAvailabilityItems = useMemo(() => {
    if (!allSLBPositions || allSLBPositions.length === 0) return [];
    return groupInventoryBySymbol(allInventoryData || [], 0);
  }, [allSLBPositions, allInventoryData]);
  const symbolToPositionMap = useMemo(() => {
    const map = new Map<string, SLBPosition>();
    allSLBPositions.forEach(pos => {
      map.set(pos.symbolId, pos);
    });
    return map;
  }, [allSLBPositions]);
  const symbolCounterpartyMap = useMemo(() => {
    const map = new Map<string, SLBPosition>();
    allSLBPositions.forEach(pos => {
      const key = `${pos.symbolId}-${pos.counterparty}`;
      if (!map.has(key)) {
        map.set(key, pos);
      }
    });
    return map;
  }, [allSLBPositions]);
  const availabilityItemsForSearch = useMemo(() => {
    return groupedAvailabilityItems.map((groupedItem): SearchItem => {
      const inventoryItem = symbolToPositionMap.get(groupedItem.symbol_id);
      const firstCounterparty = groupedItem.counterparties[0];
      const counterpartyPosition = firstCounterparty?.name
        ? symbolCounterpartyMap.get(`${groupedItem.symbol_id}-${firstCounterparty.name}`)
        : null;
      return {
        groupedItem,
        item: {
          legalEntity: counterpartyPosition?.legalEntity || inventoryItem?.legalEntity || '',
          accountId: counterpartyPosition?.accountId || inventoryItem?.accountId || '',
          counterparty: firstCounterparty?.name || inventoryItem?.counterparty || '',
          type: (inventoryItem?.type as 'STOCK' | 'BOND' | 'ETF') || 'STOCK',
          sedol: groupedItem.sedol,
          isin: groupedItem.isin,
          symbolId: groupedItem.symbol_id,
          shortName: inventoryItem?.shortName || groupedItem.symbol_id,
          price: inventoryItem?.price || 0,
          currency: inventoryItem?.currency || 'USD',
          quantity: groupedItem.quantity,
          benchmarkFee: groupedItem.benchmark_fee,
        }
      };
    });
  }, [groupedAvailabilityItems, symbolToPositionMap, symbolCounterpartyMap]);
  const searchIndex = useMemo(() => {
    return availabilityItemsForSearch.map((entry, index) => ({
      index,
      searchableText: [
        entry.item.symbolId.toLowerCase(),
        entry.item.shortName.toLowerCase(),
        entry.item.isin.toLowerCase(),
        entry.item.sedol.toLowerCase(),
      ].join(' '),
      entry,
    }));
  }, [availabilityItemsForSearch]);
  const filteredInventoryItems = useMemo(() => {
    const normalizedQuery = securitySearchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }
    return searchIndex
      .filter(({ searchableText }) => searchableText.includes(normalizedQuery))
      .map(({ entry }) => entry)
      .slice(0, 50);
  }, [securitySearchQuery, searchIndex]);

  return { allSLBPositions, filteredInventoryItems, isDataLoading };
}
